import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import { buildEventListItem } from '@/utils/eventSchema';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  MapPin, 
  ArrowRight, 
  Loader2, 
  Layers,
  Building2,
  Clock,
  History,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GA Event Tracking
 */
const trackEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  } else {
    console.log('GA Event:', eventName, params);
  }
};

/**
 * SeriesEventsPage - SEO-optimized event series listing
 * Route: /events/series/:seriesKey
 */
export default function SeriesEventsPage() {
  const { seriesKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gone, setGone] = useState(false);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const fetchSeriesEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/series/${seriesKey}`);
        setData(response.data);
        
        // Track page view
        trackEvent('seo_page_viewed', {
          page_type: 'series',
          seriesKey: seriesKey,
          edition_count: response.data.totalEditions
        });
        
        if (!response.data.hasEvents) {
          trackEvent('seo_empty_state_viewed', {
            page_type: 'series',
            seriesKey: seriesKey
          });
        }
      } catch (err) {
        console.error('Error fetching series events:', err);
        if (err?.response?.status === 410) {
          setGone(true);
        } else {
          setError('Unable to load this event series.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSeriesEvents();
  }, [seriesKey]);

  // Generate SEO metadata
  const getPageMeta = () => {
    const seriesName = data?.series?.name || seriesKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return {
      title: `${seriesName} - All Editions | Jigsaw Conferences`,
      description: `View all editions of ${seriesName}. ${data?.totalEditions || 0} editions from ${data?.series?.organiser || 'leading organisers'}. Find upcoming dates, venues and booking information.`,
      h1: seriesName
    };
  };

  const pageMeta = getPageMeta();

  // Generate JSON-LD schema
  const generateSchema = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageMeta.title,
      "description": pageMeta.description,
      "url": `https://events.jigsawconferences.co.uk/events/series/${seriesKey}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type": "ListItem", "position": 1, "name": "Events", "item": "https://events.jigsawconferences.co.uk/events"},
          {"@type": "ListItem", "position": 2, "name": "Series", "item": "https://events.jigsawconferences.co.uk/events"},
          {"@type": "ListItem", "position": 3, "name": data?.series?.name || seriesKey}
        ]
      }
    };
    
    const allEvents = [...(data?.upcomingEvents || []), ...(data?.pastEvents || [])];
    if (allEvents.length > 0) {
      schema.mainEntity = {
        "@type": "ItemList",
        "numberOfItems": allEvents.length,
        "itemListElement": allEvents.slice(0, 10).map((event, index) => buildEventListItem(event, index + 1))
      };
    }
    
    return schema;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading series...</span>
        </div>
      </MainLayout>
    );
  }

  if (gone) {
    return (
      <MainLayout>
        <NotFoundState
          title="This event series has been removed"
          description="This series is no longer available on Jigsaw Events Hub."
        />
      </MainLayout>
    );
  }

  const upcomingEvents = data?.upcomingEvents || [];
  const pastEvents = data?.pastEvents || [];

  return (
    <MainLayout>
      <SEOHead
        title={pageMeta.title}
        description={pageMeta.description}
        url={`https://events.jigsawconferences.co.uk/events/series/${seriesKey}`}
        schema={generateSchema()}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-teal-200 text-sm mb-4">
            <Link to="/events" className="hover:text-white">Events</Link>
            <span className="mx-2">›</span>
            <span className="text-white">Series</span>
            <span className="mx-2">›</span>
            <span className="text-white">{data?.series?.name || seriesKey}</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Layers className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{pageMeta.h1}</h1>
              {data?.series?.organiser && (
                <p className="text-teal-200 mt-1">by {data.series.organiser}</p>
              )}
            </div>
          </div>
          
          {data?.series?.description && (
            <p className="text-lg text-teal-100 max-w-2xl mb-6">
              {data.series.description}
            </p>
          )}
          
          {/* Quick stats */}
          <div className="flex flex-wrap gap-4">
            <Badge className="bg-white/20 text-white px-4 py-2">
              <Layers className="w-4 h-4 mr-2" />
              {data?.totalEditions || 0} Editions
            </Badge>
            {data?.series?.venue && (
              <Badge className="bg-white/20 text-white px-4 py-2">
                <Building2 className="w-4 h-4 mr-2" />
                {data.series.venue}
              </Badge>
            )}
            {data?.series?.city && (
              <Badge className="bg-white/20 text-white px-4 py-2">
                <MapPin className="w-4 h-4 mr-2" />
                {data.series.city}
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {error ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{error}</p>
            </div>
          ) : !data?.hasEvents ? (
            <div className="text-center py-12">
              <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">Series Not Found</h2>
              <p className="text-gray-500 mb-6">We couldn&apos;t find any events in this series.</p>
              <Link to="/events">
                <Button className="bg-orange-600 hover:bg-orange-700">
                  Browse All Events
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Upcoming */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Clock className="w-6 h-6 mr-2 text-teal-600" />
                  Upcoming Editions
                </h2>
                
                {upcomingEvents.length === 0 ? (
                  <Card className="bg-gray-50">
                    <CardContent className="p-8 text-center">
                      <p className="text-gray-500">No upcoming editions announced yet.</p>
                      <p className="text-sm text-gray-400 mt-2">Check back soon or browse past editions below.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {upcomingEvents.map((event) => (
                      <Link
                        key={event.slug}
                        to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                        className="block group"
                      >
                        <Card className="h-full hover:shadow-lg transition-shadow border-teal-200 hover:border-teal-500 bg-gradient-to-br from-teal-50 to-white">
                          <CardContent className="p-6">
                            <Badge className="bg-teal-100 text-teal-800 mb-3">Upcoming</Badge>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-teal-600">
                              {event.name}
                            </h3>
                            
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center text-gray-600">
                                <Calendar className="w-5 h-5 mr-2 text-teal-500" />
                                <span className="font-medium">{event.dates?.display || event.dates?.start}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <MapPin className="w-5 h-5 mr-2 text-teal-500" />
                                <span>{event.venueName}, {event.city}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center text-sm font-medium text-teal-600 group-hover:text-teal-700">
                              View event details
                              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Past Editions */}
              {pastEvents.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                      <History className="w-6 h-6 mr-2 text-gray-400" />
                      Past Editions
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPast(!showPast)}
                    >
                      {showPast ? 'Hide' : 'Show'} Past Editions ({pastEvents.length})
                    </Button>
                  </div>
                  
                  {showPast && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {pastEvents.map((event) => (
                        <Link
                          key={event.slug}
                          to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                          className="block group"
                        >
                          <Card className="h-full hover:shadow-md transition-shadow border-gray-200 opacity-75 hover:opacity-100">
                            <CardContent className="p-4">
                              <Badge variant="secondary" className="mb-2 text-xs">Past</Badge>
                              <h3 className="font-medium text-gray-700 mb-2 group-hover:text-teal-600 line-clamp-1">
                                {event.name}
                              </h3>
                              <div className="flex items-center text-sm text-gray-500">
                                <Calendar className="w-4 h-4 mr-1" />
                                {event.dates?.display || event.dates?.start}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Subscribe CTA */}
      {data?.series?.organiser && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Stay Updated on {data.series.name}
            </h2>
            <p className="text-gray-600 mb-6">
              Subscribe to get notified when new editions are announced.
            </p>
            <a href={`${BACKEND_URL}/api/public/calendar.ics?series=${seriesKey}`} rel="nofollow noindex">
              <Button className="bg-teal-600 hover:bg-teal-700">
                <Calendar className="w-4 h-4 mr-2" />
                Add Series to Calendar
              </Button>
            </a>
          </div>
        </section>
      )}

      {/* Event Boost CTA */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Attending {data?.series?.name || 'this series'}?</p>
                <p className="text-sm text-gray-600">Share with colleagues and earn referral rewards!</p>
              </div>
            </div>
            <Link to="/boost/dashboard">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white whitespace-nowrap">
                Share & Earn
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Events */}
      <section className="py-8 bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/events" className="inline-flex items-center text-gray-600 hover:text-teal-600">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Back to all events
          </Link>
        </div>
      </section>
    </MainLayout>
  );
}
